import {
    useState,
    useRef,
    createRef,
} from 'react'

import {
    useQuery,
    useMutation,
} from '@tanstack/react-query'

import {
    useLocation,
    useNavigate,
} from 'react-router-dom'

import PublicHeader from '../../publicHeader'
import BackgroundCheckStepper from '../BackgroundCheckForm/Components/BackgroundCheckStepper'

import preCheckFormQuery from './services/pre-check-form'
import preCheckFormUpdateIdQuery from './services/pre-check-form-update-id'
import savePreCheckConsentProgressMutation from './services/save-pre-check-consent-progress'
import consentUploadMutation from './services/consent-upload'
import startPreCheckSessionMutation from './services/start-pre-check-session'
import submitPreCheckFormMutation from './services/submit-pre-check-form'

import Overlay from '../../Components/Overlay'
import FullScreenDialog from '../../Components/FullScreenDialog'

import { getElementWithInlineStyles } from '../../helpers/component-helper'

import WelcomeAndStatusSelection from './Components/WelcomeAndStatusSelection'
import LegalDisclosuresAndConsentHub from './Components/LegalDisclosuresAndConsentHub'
import SignatureAndIdentifier from './Components/SignatureAndIdentifier'
import PreCheck from './Components/PreCheck'
import AdditionalDocument from './Components/AdditionalDocument'

const PreCheckForm = () => {

    const location = useLocation()

    const navigate = useNavigate()

    const refToPrint = useRef()

    const queryParams = new URLSearchParams(location.search)
    const key = queryParams.get('key')

    const {
        data,
        error,
        isLoading,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['pre-check-form', key],
        queryFn: () => preCheckFormQuery(key),
        refetchOnWindowFocus: false,
    })

    const dataRef = useRef(data)
    dataRef.current = data

    const formUpdateIdRef = useRef(data?.formUpdateId)
    formUpdateIdRef.current = data?.formUpdateId

    const {
        data: formUpdateIdData,
        isFetching: formUpdateIdIsFetching,
        refetch: refetchFormUpdateId,
    } = useQuery({
        queryKey: ['pre-check-form-update-id', key],
        queryFn: () => preCheckFormUpdateIdQuery(key, formUpdateIdRef.current),
        enabled: false,
        retry: false,
    })

    const {
        error: savePreCheckConsentProgressError,
        isPending: savePreCheckConsentProgressIsPending,
        mutate: savePreCheckConsentProgress,
        mutateAsync: savePreCheckConsentProgressAsync,
    } = useMutation({
        mutationFn: savePreCheckConsentProgressMutation,
        onSuccess: async (d) => {

            if (
                currentPage.type === "dl_ssn_card" ||
                currentPage.type === "dl_birth_cert" ||
                currentPage.type === "dl_naturalization" ||
                currentPage.type === "foreign_passport_i551_stamp" ||
                currentPage.type === "i551_expired_i797" ||
                currentPage.type === "ead_expired_i797c" ||
                currentPage.type === "foreign_passport_i94" ||
                currentPage.type === "foreign_passport_i797a" ||
                currentPage.type === "additional_document"
            ) {
                preCheckOnSubmitted()
                return
            }

            const nextPageIndex = currentPageIndex + 1

            if (nextPageIndex === preCheckPageIndex) {
                startPreCheckSession({
                    key,
                })
                return
            }

            if (currentPageIndex === welcomeAndStatusSelectionPageIndex) {
                await refetch()
            }

            // continue next page
            setCurrentPageIndex(nextPageIndex)
            setCurrentStep(nextPageIndex)
        }
    })

    const {
        error: mutationSubmitError,
        isPending: mutationSubmitIsPending,
        mutate: submitPreCheckForm,
        data: mutationSubmitData,
    } = useMutation({
        mutationFn: submitPreCheckFormMutation,
        onSuccess: () => {
            navigate('/pre-check-form-success')
        }
    })

    const {
        error: mutationConsentUploadError,
        isPending: mutationConsentUploadIsPending,
        mutate: consentUpload,
        data: mutationConsentUploadData,
    } = useMutation({
        mutationFn: consentUploadMutation,
        onSuccess: async (d) => {
            const payload = d.payloadToSaveApplicantProgress
            const pageType = d.pageType
            const url = d.url

            const page = payload?.data?.find((d => d.type === pageType))

            if (page?.data) {
                page.data.htmlUrl = url
            }

            savePreCheckConsentProgress(payload)
        },
        onError: () => {
            saveAdditionalPageRef.current = true
        }
    })

    const {
        isPending: startPreCHeckSessionIsPending,
        mutate: startPreCheckSession,
    } = useMutation({
        mutationFn: startPreCheckSessionMutation,
        onSuccess: async (d) => {

            const nextPageIndex = currentPageIndex + 1

            await refetch()

            // continue next page
            setCurrentPageIndex(nextPageIndex)
            setCurrentStep(nextPageIndex)

        }
    })

    const [currentPageIndex, setCurrentPageIndex] = useState(0)
    const [currentStep, setCurrentStep] = useState(0)
    const [showRetryDialog, setShowRetryDialog] = useState(false)

    const pageRefs = useRef([])

    const saveAdditionalPageRef = useRef(true)

    const handleComplete = () => {
        navigate('/pre-check-form-success')
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen my-4">
                <span className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
            </div>
        )
    }
    if (error) {
        return (
            <div>Error: {error.message}</div>
        )
    }

    if (!data) {
        return (
            <div>
                Data is not loaded yet.
            </div>
        )
    }

    if (data?.noFormMessage) {

        if (data?.status === "expired") {
            return (
                <div className="h-screen flex flex-col bg-gray-50">

                    <PublicHeader />

                    <div className="flex-grow flex items-center justify-center">
                        Expired
                    </div>

                </div>

            )
        }

        return (
            <div className="h-screen flex flex-col bg-gray-50">

                <PublicHeader />

                <p className="mt-1 text-base py-4 mx-auto">
                    {data.noFormMessage}
                </p>

            </div>
        )
    }

    const currentPage = data.Pages[currentPageIndex]

    let currentRef = pageRefs.current[currentPageIndex]

    if (!currentRef) {
        currentRef = createRef()
        pageRefs.current[currentPageIndex] = currentRef
    }

    const steps = data.Pages.map(p => p.title)

    const handlePrev = () => {
        const prevPageIndex = currentPageIndex - 1
        if (prevPageIndex < 0) {
            return
        }
        setCurrentPageIndex(prevPageIndex)

        setCurrentStep(prevPageIndex)

    }

    let nextButtonText = (
        currentPageIndex === data.Pages.length - 1 &&
        currentPage.type !== 'pre_check'
    ) ? "Submit" : "Next"

    const preCheckPageIndex = data.Pages.findIndex(page => page.type === "pre_check")
    const welcomeAndStatusSelectionPageIndex = data.Pages.findIndex(page => page.type === "welcome_and_status_selection")

    const handleNext = () => {

        const nextPageIndex = currentPageIndex + 1
        const currentPageData = currentRef.current.validateAndGetData()
        if (!currentPageData.isValid) {
            return
        }

        const activeData = dataRef.current
        const activePage = activeData.Pages[currentPageIndex]
        activePage.data = currentPageData.value

        const dataToSubmit = activeData.Pages.map(page => {
            return {
                type: page.type,
                data: page.data,
            }
        })

        const payload = {
            key,
            data: dataToSubmit,
        }

        if (
            currentPage.type === "dl_ssn_card" ||
            currentPage.type === "dl_birth_cert" ||
            currentPage.type === "dl_naturalization" ||
            currentPage.type === "foreign_passport_i551_stamp" ||
            currentPage.type === "i551_expired_i797" ||
            currentPage.type === "ead_expired_i797c" ||
            currentPage.type === "foreign_passport_i94" ||
            currentPage.type === "foreign_passport_i797a" ||
            currentPage.type === "additional_document"
        ) {
            if (saveAdditionalPageRef.current) {
                saveAdditionalPageRef.current = false
                savePreCheckConsentProgress(payload)
                return
            }
            else {
                saveAdditionalPageRef.current = true
            }

        }

        if (nextPageIndex >= activeData.Pages.length) {

            submitPreCheckForm(payload)
            return
        }

        if (refToPrint.current) {

            syncCheckedAttributes(refToPrint.current)

            consentUpload({
                dataForUploadUrl: {
                    key,
                    consentType: activePage.type,
                },
                file: new File([getElementWithInlineStyles(refToPrint.current),], `${activePage.type}.html`, { type: "text/html" }),
                payloadToSaveApplicantProgress: payload,
                pageType: activePage.type,
            })

            return
        }

        const activePreCheckPageIndex = activeData.Pages.findIndex(page => page.type === "pre_check")

        if (nextPageIndex === activePreCheckPageIndex) {
            savePreCheckConsentProgress(payload)
            return
        }

        // continue next page
        setCurrentPageIndex(nextPageIndex)
        setCurrentStep(nextPageIndex)

    }

    const preCheckOnSubmitted = async () => {

        try {
            await refetchFormUpdateId({ throwOnError: true })
        }
        catch (e) {
            setShowRetryDialog(true)
            return
        }

        const { data: freshData } = await refetch()

        dataRef.current = freshData

        handleNext()

    }


    return (
        <div className="h-screen flex flex-col bg-gray-50">

            <PublicHeader referenceNumber={data.referenceNumber} />

            <BackgroundCheckStepper steps={steps} currentStep={currentStep} />

            <div className="flex flex-col flex-grow items-center justify-center">
                {
                    currentPage.type === "welcome_and_status_selection" &&
                    <WelcomeAndStatusSelection
                        ref={currentRef}
                        data={currentPage.data}
                        handleNext={handleNext}
                        refToPrint={refToPrint}
                    />
                }
                {
                    currentPage.type === "legal_disclosures_and_consent_hub" &&
                    <LegalDisclosuresAndConsentHub
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                        company={currentPage.company}
                        handleNext={handleNext}
                    />
                }
                {
                    currentPage.type === "signature_and_identifier" &&
                    <SignatureAndIdentifier
                        ref={currentRef}
                        data={currentPage.data}
                        refToPrint={refToPrint}
                        company={currentPage.company}
                        formKey={key}
                        handleNext={handleNext}
                        refetch={refetch}
                    />
                }
                {
                    currentPage.type === "pre_check" &&
                    <PreCheck
                        ref={currentRef}
                        data={currentPage.data}
                        company={currentPage.company}
                        onSubmitted={() => preCheckOnSubmitted()}
                        documentText={currentPage.documentText}
                    />
                }

                {
                    (
                        currentPage.type === "dl_ssn_card" ||
                        currentPage.type === "dl_birth_cert" ||
                        currentPage.type === "dl_naturalization" ||
                        currentPage.type === "foreign_passport_i551_stamp" ||
                        currentPage.type === "i551_expired_i797" ||
                        currentPage.type === "ead_expired_i797c" ||
                        currentPage.type === "foreign_passport_i94" ||
                        currentPage.type === "foreign_passport_i797a" ||
                        currentPage.type === "additional_document"
                    ) &&
                    <AdditionalDocument
                        key={currentPage.documentId}
                        ref={currentRef}
                        data={currentPage.data}
                        requestKey={key}
                        title={currentPage.title}
                        description={currentPage.description}
                        documentId={currentPage.documentId}
                        examples={currentPage.examples}
                        analyzeId={currentPage.analyzeId}
                    />
                }

            </div>



            {(false) && (
                <p className="mt-1 text-base text-red-600 py-4 mx-auto">
                    Error
                </p>
            )}

            {
                (
                    currentPage.type !== "welcome_and_status_selection" &&
                    currentPage.type !== "signature_and_identifier"
                ) && (
                    <div className="flex justify-end gap-6 p-4 border-t">
                        <button
                            onClick={handlePrev}
                            disabled={currentPageIndex === 0}
                            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            Prev
                        </button>
                        <button
                            onClick={handleNext}
                            className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed ${currentPage.type === 'pre_check' ? 'invisible pointer-events-none' : ''}`}
                        >
                            {nextButtonText}
                        </button>
                    </div>
                )
            }

            {/* Overlay */}
            {
                (
                    isFetching ||
                    savePreCheckConsentProgressIsPending ||
                    startPreCHeckSessionIsPending ||
                    mutationConsentUploadIsPending ||
                    mutationSubmitIsPending ||
                    formUpdateIdIsFetching
                ) &&
                <Overlay />
            }

            {
                showRetryDialog &&
                <FullScreenDialog
                    text="Results could not be retrieved yet. Please try again."
                    submitText="Retry"
                    onSubmit={() => {
                        setShowRetryDialog(false)
                        preCheckOnSubmitted()
                    }}
                />
            }

            {
                mutationSubmitData &&
                <FullScreenDialog
                    text={
                        <>
                            <div>
                                <div className="mb-4 font-semibold text-center text-xlg">
                                    Thank you for completing your Pre Check application!
                                </div>
                                <div className="mb-2 text-sm">
                                    Your information has been successfully submitted to VerifiedID for processing.
                                </div>
                                <div className="mb-4 text-sm">
                                    We will notify you once the pre check is complete, or if any additional information is required.
                                </div>
                                <div className='mb-4 text-sm'>
                                    You may now close this window.
                                </div>
                            </div>
                        </>
                    }
                    onSubmit={handleComplete}
                    submitText="Close Window"
                />
            }

        </div>

    )
}

export default PreCheckForm

function syncCheckedAttributes(container) {
    const inputs = container.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach((input) => {
        if (input.checked) {
            input.setAttribute("checked", "checked");
        } else {
            input.removeAttribute("checked");
        }
    });
}
